import { useCallback, useEffect, useRef, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type {
  ConsumptionReport,
  NewReport,
  ReportAccount,
  ReportOptions,
} from '../api/monitor';
import { isPending } from '../lib/reports';

/**
 * De quanto em quanto tempo a tela pergunta se a geração terminou.
 *
 * Só roda enquanto existe relatório em andamento; parado, a tela não conversa
 * com o servidor. Coletar centenas de linhas na Vivo leva minutos, então cinco
 * segundos é frequente o bastante para parecer vivo sem virar enxurrada.
 */
const POLL_MS = 5_000;

export interface ReportsController {
  options: ReportOptions | null;
  /** Contas do master escolhido; vazio enquanto não há master. */
  accounts: ReportAccount[];
  reports: ConsumptionReport[];
  loading: boolean;
  /** Falha ao carregar a tela (não a de gerar, que volta pelo `create`). */
  error: string | null;
  creating: boolean;
  selectMaster: (masterId: number | null) => void;
  masterId: number | null;
  create: (payload: NewReport) => Promise<void>;
  reload: () => void;
}

export function useReports(): ReportsController {
  const [options, setOptions] = useState<ReportOptions | null>(null);
  const [accounts, setAccounts] = useState<ReportAccount[]>([]);
  const [masterId, setMasterId] = useState<number | null>(null);
  const [reports, setReports] = useState<ConsumptionReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Trocar de master rápido faz as respostas voltarem fora de ordem; só a
  // última pedida pode escrever a lista de contas.
  const latestAccounts = useRef(0);

  useEffect(() => {
    let active = true;

    setLoading(true);

    Promise.all([monitorApi.reportOptions(), monitorApi.reports()])
      .then(([loadedOptions, loadedReports]) => {
        if (!active) return;

        setOptions(loadedOptions);
        setReports(loadedReports.reports);
        setError(null);
      })
      .catch((cause: Error) => {
        if (active) setError(cause.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    if (masterId === null) {
      setAccounts([]);
      return;
    }

    const requestId = latestAccounts.current + 1;
    latestAccounts.current = requestId;

    monitorApi
      .reportAccounts(masterId)
      .then((response) => {
        if (latestAccounts.current === requestId) setAccounts(response.accounts);
      })
      .catch(() => {
        if (latestAccounts.current === requestId) setAccounts([]);
      });
  }, [masterId]);

  // Enquanto houver relatório em andamento, repergunta a lista inteira. É uma
  // consulta barata (uma tabela, LIMIT fixo) e evita ter de casar respostas
  // individuais com a lista que já está na tela.
  useEffect(() => {
    if (!reports.some(isPending)) return;

    const timer = window.setInterval(() => {
      monitorApi
        .reports()
        .then((response) => setReports(response.reports))
        .catch(() => undefined);
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [reports]);

  const create = useCallback(async (payload: NewReport) => {
    setCreating(true);

    try {
      const response = await monitorApi.createReport(payload);
      setReports(response.reports);
    } finally {
      setCreating(false);
    }
  }, []);

  const selectMaster = useCallback((next: number | null) => setMasterId(next), []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return {
    options,
    accounts,
    reports,
    loading,
    error,
    creating,
    masterId,
    selectMaster,
    create,
    reload,
  };
}
